import { ErrorCode } from '@approvalmax/types';
import { toast } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { loadCreateAccount } from 'modules/first-start';
import { useDispatch } from 'modules/react-redux';
import { useUserProfile } from 'modules/utils';
import { useCallback, useEffect } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { useTakeOverOrganisation } from 'shared/data/myAccount/v1';

import { PopupDeleteOrganisationFormData } from '../PopupDeleteOrganisation/PopupDeleteOrganisation.types';
import { popupTransferImpossibleState } from '../PopupTransferImpossible/PopupTransferImpossible.states';
import { messages } from './PopupTransferOrganisation.messages';
import { popupTransferOrganisationState } from './PopupTransferOrganisation.states';
import { PopupTransferOrganisationFormData } from './PopupTransferOrganisation.types';

export const useTransferOrganisationPopupData = (company: selectors.types.ExpandedCompany) => {
    const dispatch = useDispatch();
    const profile = useUserProfile();

    const hasAccount = !!profile.account;

    const toggleTransferPopup = useSetAtom(popupTransferOrganisationState);
    const toggleTransferImpossiblePopup = useSetAtom(popupTransferImpossibleState);

    const { mutateAsync, isLoading, isSuccess, isError, error } = useTakeOverOrganisation();

    const form = useForm<PopupTransferOrganisationFormData>({
        defaultValues: {
            agreed: false,
        },
        mode: 'onChange',
        resolver: (values) => {
            const errors: FieldErrors<PopupDeleteOrganisationFormData> = {};

            if (values.agreed !== hasAccount) {
                errors.agreed = {
                    type: 'required',
                };
            }

            return {
                values: Object.keys(errors).length > 0 ? {} : values,
                errors,
            };
        },
    });

    const onTransfer = useCallback(async () => {
        if (!hasAccount) {
            dispatch(
                loadCreateAccount({
                    isPartner: false,
                    practiceName: '',
                    companyCount: '',
                    clientCount: '',
                })
            );
        }

        await mutateAsync({
            body: {
                organisationId: company.id,
                accountId: profile.account?.accountId || '',
            },
            params: {
                query: {
                    accountId: profile.account?.accountId || '',
                },
            },
        });
    }, [company.id, dispatch, hasAccount, profile.account, mutateAsync]);

    useEffect(() => {
        if (isSuccess) {
            toast.success(messages.takeOverOrganisationTransferSuccess);

            toggleTransferPopup(false);
        }
    }, [isSuccess, toggleTransferPopup]);

    useEffect(() => {
        if (isError) {
            switch (error.code) {
                case ErrorCode.E12003_TARGET_USER_IS_XERO_REFERRAL:
                    toggleTransferImpossiblePopup(true);
                    break;
            }
        }
    }, [isError, error, toggleTransferImpossiblePopup]);

    return {
        form,
        onFormSubmit: form.handleSubmit(onTransfer),

        hasAccount,
        canTransfer: !isLoading && form.formState.isDirty && Object.keys(form.formState.errors).length === 0,
    };
};
